import { Button, Text, toast } from "@medusajs/ui"
import { useSearchParams } from "react-router-dom"
import { DataTable, EmptyState, ErrorState, PageHeader, SearchInput, Section, Thumb, usePage } from "@/components/ui"
import { useAbandoned, useSendRecovery, type Abandoned } from "@/lib/abandoned"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { formatDate, formatMoney, relativeTime } from "@/lib/format"

const PAGE = 50
const summary = (c: Abandoned) => c.items.map((i) => `${i.title} × ${i.quantity}`).join(", ")

export function AbandonedPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const { data, isLoading, error } = useAbandoned({ q, limit: PAGE, offset: pageIndex * PAGE })
  const send = useSendRecovery()
  const canSend = useCan()("orders:write")
  if (error) return <ErrorState error={error} />
  const recover = (c: Abandoned) => send.mutate(c.id, { onSuccess: () => toast.success(`Recovery email sent to ${c.email}`), onError: (e) => toast.error(errorMessage(e)) })
  const status = (c: Abandoned) => (c.recovery_sent_at ? `Emailed ${relativeTime(c.recovery_sent_at)}${c.recovery_count > 1 ? ` (${c.recovery_count}×)` : ""}` : "Not emailed")
  const action = (c: Abandoned) =>
    canSend ? (
      <Button size="small" variant="secondary" disabled={send.isPending && send.variables === c.id} onClick={(e) => { e.stopPropagation(); recover(c) }}>
        {c.recovery_count ? "Send again" : "Send email"}
      </Button>
    ) : null
  return (
    <>
      <PageHeader title="Abandoned checkouts" />
      <Section>
        <div className="px-4 py-3"><SearchInput value={q} onChange={setQ} placeholder="Search by email" /></div>
        <DataTable<Abandoned>
          rows={data?.checkouts}
          count={data?.count ?? 0}
          pageSize={PAGE}
          pageIndex={pageIndex}
          onPageChange={setPage}
          loading={isLoading}
          rowKey={(c) => c.id}
          empty={<EmptyState title="No abandoned checkouts" body="When a customer leaves checkout with their email entered, it shows up here." />}
          columns={[
            { key: "date", header: "Last activity", cell: (c) => <span title={formatDate(c.updated_at)}>{relativeTime(c.updated_at)}</span> },
            { key: "customer", header: "Customer", cell: (c) => <div><div>{c.customer_name ?? c.email}</div>{c.customer_name && <div className="text-[12px] text-ui-fg-subtle">{c.email}</div>}</div> },
            { key: "location", header: "Location", cell: (c) => c.location ?? "—" },
            { key: "items", header: "Items", cell: (c) => <div className="flex items-center gap-2"><Thumb src={c.items[0]?.thumbnail} size={28} /><span className="max-w-[260px] truncate">{summary(c)}</span></div> },
            { key: "total", header: "Total", className: "text-right", cell: (c) => formatMoney(c.total, c.currency_code) },
            { key: "recovery", header: "Recovery email", cell: (c) => <div className="flex items-center justify-end gap-3"><span className="text-[12px] text-ui-fg-subtle">{status(c)}</span>{action(c)}</div> },
          ]}
          mobile={(c) => (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0"><Text size="small" weight="plus" className="truncate">{c.customer_name ?? c.email}</Text><Text size="xsmall" className="truncate text-ui-fg-subtle">{summary(c)} · {relativeTime(c.updated_at)}</Text></div>
                <Text size="small">{formatMoney(c.total, c.currency_code)}</Text>
              </div>
              <div className="flex items-center justify-between gap-2"><span className="text-[12px] text-ui-fg-subtle">{status(c)}</span>{action(c)}</div>
            </div>
          )}
        />
      </Section>
    </>
  )
}
